import { logger } from "./logger";

/**
 * Record what a `setCommon` dispatch is about to do to the cuadre-scoped slices.
 *
 * Each of these slices keeps its own `cuadre_id` and clears itself on `setCommon`
 * unless that id matches the incoming one (newLoans.js:81, payments.js:51,
 * newDebtors.js:42, loans.js:228):
 *
 *     if (state.cuadre_id === payload.cuadre_id) return state;
 *     return { ...initialState, cuadre_id: payload.cuadre_id };
 *
 * That branch is the only place the entregas and cobros lists are emptied in
 * memory, and it left no trace, so "the app deleted my data" could not be
 * confirmed or ruled out after the fact. Call this immediately BEFORE dispatching
 * `setCommon`, while the old state is still readable.
 *
 * Two actions, so the answer is greppable and needs no interpretation:
 *   cuadre.slices_wiped (warning) — rows really were destroyed, with the count.
 *   cuadre.slices_kept  (info)    — the guard held, nothing was lost.
 *
 * `id_types` matters more than it looks. The reducer compares with `===`, so a
 * string "170731" and a number 170731 never match, and the guard would then fail
 * on EVERY dispatch and wipe the lists every time. The comparison here is
 * deliberately the same `!==`, not a normalised one — a log that disagrees with
 * the reducer it describes is worse than no log.
 *
 * Never throws: this is diagnostics on the login path.
 *
 * @param {object} state              store.getState() taken before the dispatch
 * @param {string|number|null} incomingCuadreId  payload.cuadre_id about to be sent
 * @param {string} where              which call site ("login" | "recovery")
 */
const CUADRE_SCOPED_SLICES = [
  { slice: "newLoans", rows: "newLoans" },
  { slice: "payments", rows: "payments" },
  { slice: "newDebtors", rows: "newDebtors" },
  { slice: "loans", rows: "loans" },
];

export function logCuadreSliceReset(state, incomingCuadreId, where) {
  try {
    const slices = CUADRE_SCOPED_SLICES.map(({ slice, rows }) => {
      const current = state?.[slice];
      const sliceCuadreId = current?.cuadre_id ?? null;
      const rowCount = Array.isArray(current?.[rows]) ? current[rows].length : 0;
      const willReset = sliceCuadreId !== incomingCuadreId;

      return {
        slice,
        slice_cuadre_id: sliceCuadreId,
        row_count: rowCount,
        will_reset: willReset,
        rows_destroyed: willReset ? rowCount : 0,
      };
    });

    const rowsDestroyed = slices.reduce((total, s) => total + s.rows_destroyed, 0);

    const context = {
      where,
      incoming_cuadre_id: incomingCuadreId ?? null,
      id_types: {
        incoming: typeof incomingCuadreId,
        slices: [...new Set(slices.map((s) => typeof s.slice_cuadre_id))],
      },
      rows_destroyed: rowsDestroyed,
      slices,
    };

    if (rowsDestroyed > 0) {
      logger.warning(
        "cuadre.slices_wiped",
        `setCommon cleared ${rowsDestroyed} in-memory rows (cuadre changed)`,
        { entity: "cuadre", context }
      );
      return;
    }

    logger.info("cuadre.slices_kept", "setCommon kept the cuadre slices (no rows lost)", {
      entity: "cuadre",
      context,
    });
  } catch (e) {
    // Diagnostics must never break login or the recovery path.
  }
}

export default logCuadreSliceReset;
